import React, { useState, useEffect } from 'react';
import { Card, Form, Button, Alert, Badge, Row, Col } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import PackingItem from './PackingItem';

/**
 * PackingList component for managing items to pack for a trip
 * 
 * @param {Object} props Component props
 * @param {Array} props.items List of packing items
 * @param {Function} props.onItemChange Handler for when an item is changed
 * @param {Function} props.onItemDelete Handler for when an item is deleted
 * @param {Function} props.onItemAdd Handler for when an item is added
 * @param {Function} props.onItemEdit Handler for when an item is edited
 * @param {string} props.title Title for the packing list
 * @param {boolean} props.hideHeader Whether to hide the header
 * @param {string} props.category Current category filter
 * @param {Function} props.onCategoryChange Handler for when category filter changes
 */
const PackingList = ({ 
  items = [], 
  onItemChange, 
  onItemDelete, 
  onItemAdd,
  onItemEdit,
  title,
  hideHeader = false,
  categories = ["All", "Clothing", "Electronics", "Toiletries", "Documents", "Miscellaneous"],
  category = "All",
  onCategoryChange,
  readOnly = false
}) => {
  const { t } = useTranslation();
  const [newItemText, setNewItemText] = useState('');
  const [newItemCategory, setNewItemCategory] = useState('Clothing');
  const [error, setError] = useState('');
  
  // Use provided title or default translated title
  const displayTitle = title || t('planDetail.packing.packingList');
  
  // Get translated categories
  const getTranslatedCategories = () => {
    return categories.map(cat => {
      const categoryKey = cat.toLowerCase();
      return {
        original: cat,
        translated: t(`planDetail.packing.categories.${categoryKey}`, cat)
      };
    });
  };
  
  const translatedCategories = getTranslatedCategories();

  // Sync dropdown with filter selection (but not vice versa)
  useEffect(() => {
    // Only update if category is not "All" (since "All" is not a valid category for new items)
    if (category !== "All" && categories.includes(category)) {
      setNewItemCategory(category);
    }
  }, [category, categories]);

  const handleAddItem = () => {
    if (readOnly || !onItemAdd) return;
    if (!newItemText.trim()) {
      setError(t('planDetail.packing.pleaseEnterItem'));
      return;
    }

    const newItem = {
      id: uuidv4(),
      text: newItemText.trim(),
      createdAt: new Date().toISOString(),
      status: 'pending',
      category: newItemCategory
    };

    onItemAdd(newItem);
    setNewItemText('');
    setError('');
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddItem();
    }
  };

  // Filter items by category if a category is selected
  const filteredItems = category === "All" 
    ? items 
    : items.filter(item => item.category === category);

  // Count items by status
  const completedCount = filteredItems.filter(item => item.status === 'completed').length;
  const totalCount = filteredItems.length;

  return (
    <Card className="mb-3">
      {!hideHeader && (
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="m-0">
            <i className="bi bi-suitcase me-2"></i>
            {displayTitle}
          </h5>
          <Badge bg="primary" pill>
            {completedCount}/{totalCount}
          </Badge>
        </Card.Header>
      )}
      <Card.Body>
        {error && <Alert variant="danger" className="py-2">{error}</Alert>}
        
        {/* Category filter */}
        <div className="mb-3">
          <Row>
            <Col xs={12}>
              <div className="d-flex flex-wrap gap-2">
                {translatedCategories.map(({ original, translated }) => (
                  <Button
                    key={original}
                    variant={category === original ? "primary" : "outline-primary"}
                    size="sm"
                    onClick={() => onCategoryChange(original)}
                  >
                    {translated}
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        </div>
        
        <Form className="mb-3">
          <Row>
            <Col xs={12} md={8} className="mb-2 mb-md-0">
              <Form.Control
                type="text"
                placeholder={t('planDetail.packing.addNewItem')}
                value={newItemText}
                onChange={(e) => setNewItemText(e.target.value)}
                onKeyDown={handleInputKeyDown}
              />
            </Col>
            <Col xs={12} md={3} className="mb-2 mb-md-0">
              <Form.Select 
                value={newItemCategory}
                onChange={(e) => setNewItemCategory(e.target.value)}
              >
                {translatedCategories.filter(({ original }) => original !== "All").map(({ original, translated }) => (
                  <option key={original} value={original}>{translated}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={12} md={1}>
              <Button 
                variant="primary" 
                onClick={handleAddItem}
                className="w-100"
              >
                <i className="bi bi-plus"></i>
              </Button>
            </Col>
          </Row>
        </Form>

        <div className="packing-list">
          {filteredItems.length === 0 ? (
            <div className="text-center py-3 text-muted">
              <i className="bi bi-suitcase fs-4 mb-2 d-block"></i>
              <p className="mb-0">{t('planDetail.packing.noItemsYet')}</p>
            </div>
          ) : (
            <>
              {/* Show pending items first */}
              {filteredItems
                .filter(item => item.status === 'pending')
                .map(item => (
                  <PackingItem 
                    key={item.id} 
                    item={item} 
                    onChange={onItemChange} 
                    onDelete={onItemDelete}
                    onEdit={onItemEdit}
                    readOnly={readOnly}
                  />
                ))
              }
              
              {/* Then show packed items */}
              {filteredItems
                .filter(item => item.status === 'completed')
                .map(item => (
                  <PackingItem 
                    key={item.id} 
                    item={item} 
                    onChange={onItemChange} 
                    onDelete={onItemDelete}
                    onEdit={onItemEdit}
                    readOnly={readOnly}
                  />
                ))
              }
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  );
};

export default PackingList; 